import { z } from 'zod'

import type { PolicySourceReference } from '@/features/air/policy-management/types'

// --- wire contract (backend truth, ADR 0007) ---
// Case-list envelope. Verified equal to the backend `case` list payload ->
// alias, not authored. The 2026-07-02 capture shows `alert_description` +
// `meta_data_fields` on EVERY list row (they were previously mistyped as
// detail-only — capture-crossref §3/§4). 02 owns the case domain; this lands
// the wire target the walking-skeleton handler derives.
//
// No longer an alias: `Case.investigation` is a view model (one nullable
// `conclusion` object), while the wire keeps the serializer's flat block. See
// `InvestigationWire` / `CaseWire`.
export type CaseListWire = {
	cases: Array<CaseWire>
	/**
	 * Cases matching the filters, across all pages. Exact only on a page with
	 * rows — a page past the end says 0 — so read it from the first page.
	 * Optional: an API build older than the filter params does not send it.
	 */
	total?: number
}

/**
 *  Air Case Management
 */

export type CaseList = {
	cases: Array<Case>
	total?: number
}

export type Case = {
	tenant: string
	alert_id: string
	alert_name: string
	log_sources: {
		[key: string]: Array<string>
	}
	alert_type: string
	opened_at: string
	alert_severity: CaseSeverity
	alert_status: CaseStatus
	investigation: InvestigationSummary | null
	/** Present on every list row per the 2026-07-02 capture (crossref §3). */
	alert_description: string
	meta_data_fields: CaseMetadata | null
}

export const CaseStatus = z.enum([
	'open',
	'acknowledged',
	'closed',
	'auto-resolved',
])

export type CaseStatus = z.infer<typeof CaseStatus>

export const CaseSeverity = z.enum(['low', 'medium', 'high'])

export type CaseSeverity = z.infer<typeof CaseSeverity>

/**
 * The `case` object inside the case-detail envelope. Deliberately drops the list
 * row's `investigation`: the detail query builds its own `case_dict` (backend
 * `CaseManagementQueries.get_case`) which has NO investigation key — the
 * investigation is a SIBLING of `case`, and always was.
 */
export interface CaseDetails extends Omit<Case, 'investigation'> {
	/** Policy/rule identifier that created this case; used to pre-scope automation mapping. */
	rule_id: string | null
	/** Backend rule discriminator for `rule_id` (`custom` or OOTB). */
	rule_type: 'custom' | 'ootb' | 'ootb_rule' | null
	acknowledged_by_username: string | null
	alert_responses: {
		responses: Array<CaseResponse>
	} | null
	closed_by_username: string | null
	time_acknowledged: string
	time_closed: string
	time_opened: string
	reference?: Array<PolicySourceReference> | null
	matched_events?: Record<string, Array<string>> | null // key is [definition_id:event_type], value is document_id
	// e.g. {"034b0e74-6b05-501f-8e87-9f1f050d6986:audit":["bylt_Directory_40e09098-8997-4d1f-956f-9725e7ed1435_A61KX_4181054"]}
	enrichment_data?: Array<CaseEnrichmentResult> | null
}

/**
 * Case Enrichment
 */

export type EnrichmentProvider = 'abuseipdb' | 'ipgeolocation_io'

export type IPType =
	| 'site-local'
	| 'reserved'
	| 'loopback'
	| 'link-local'
	| 'multicast'
	| 'private'
	| 'public'
	| 'unspecified'

export type MetadataThreatClassification =
	'malicious' | 'suspicious' | 'benign' | 'unknown' | 'error'

export type IPGeolocationEnrichment = {
	enrichment_type: 'geolocation'
	provider: EnrichmentProvider
	country_code?: string | null
	country_name?: string | null
	state_prov?: string | null
	city?: string | null
	latitude?: number | null
	longitude?: number | null
	isp?: string | null
	organization?: string | null
	details?: Record<string, unknown> | null
}

export type IPSecurityEnrichment = {
	enrichment_type: 'security'
	provider: EnrichmentProvider
	threat_classification?: MetadataThreatClassification
	threat_score?: number | null
	is_tor?: boolean | null
	is_proxy?: boolean | null
	is_vpn?: boolean | null
	is_known_attacker?: boolean | null
	is_bot?: boolean | null
	total_reports?: number | null
	details?: Record<string, unknown> | null
}

export type IPEnrichmentItem = IPSecurityEnrichment | IPGeolocationEnrichment

export type IPEnrichment = {
	enrichment_metadata_type: 'ip'
	ip_address: string
	ip_version: string
	ip_type: IPType
	enrichments: Array<IPEnrichmentItem>
}

export type CaseEnrichment = IPEnrichment

export type CaseEnrichmentResult = {
	metadata_key: string
	metadata_value: string
	enrichment: CaseEnrichment
}

export type CaseResponse = {
	action_id: string | null
	playbook_id: string | null
	response_type: 'action' | 'playbook'
}

export type CaseMetadata = {
	[key: string]: Array<string | number | boolean> | null
}

export type CaseActivity = {
	alert_id: string
	new_comment: string | null
	new_status: CaseStatus
	timestamp: string
	updated_by: string
}

export type CaseDetailsResponse = {
	activity: Array<CaseActivity>
	case: CaseDetails
	/**
	 * The latest investigation conclusion, embedded in the case-detail response.
	 * `null` when the case has no investigation job (most cases / SKU off). It is
	 * a SIBLING of `case`/`activity` — read via `useCaseDetails(id).data.investigation`,
	 * NOT `data.case.investigation`. Guard before any read.
	 */
	investigation?: Investigation | null
}

export type CaseDetailsRequest = {
	status: CaseStatus
	comment: string
}

/**
 * Investigation domain
 *
 * Authoritative source: air-backend investigation models. Verified against the
 * real payloads in CASE-INVESTIGATION/research/data/{case,evidences,investigation_history}.json
 * (case 58fdbbd1-2f9a-44fc-aba7-46910c0b8003).
 */

/** Verdict — NO "unknown"/"suspicious"; uncertainty lives in confidence + behavioral_fit. */
export type Verdict = 'benign' | 'malicious'

/** Investigation job lifecycle status. */
export type InvestigationStatus =
	'pending' | 'running' | 'paused' | 'completed' | 'failed'

/** `behavior_insights.behavioral_fit` (mirrors backend `BehavioralFit`). */
export type BehavioralFit =
	| 'consistent_with_known_behavior'
	| 'mostly_consistent'
	| 'new_or_rare_behavior'
	| 'behavioral_mismatch'
	| 'insufficient_history'
	| 'likely_detection_false_positive'

/** `insight_table[].insight_area` — mirrors backend `InsightArea`. */
export type InsightArea =
	| 'actor_behavior'
	| 'source_behavior'
	| 'temporal_behavior'
	| 'target_resource_behavior'
	| 'tenant_baseline'
	| 'cross_case_pattern'
	| 'surrounding_activity'
	| 'detection_logic_fit'

/** `insight_table[].assessment` — mirrors backend `InsightAssessment`. */
export type InsightAssessment =
	| 'expected'
	| 'plausible'
	| 'rare'
	| 'new'
	| 'rule_mismatch'
	| 'insufficient_data'

/**
 * Evidence type discriminator. OPEN union — `evidence_type` includes
 * `case_similarity_search` + `sub_agent_trace` beyond the formal ORM enum, and
 * the agent may emit unknown values. Render a safe fallback for unknowns.
 */
export type EvidenceType =
	| 'event_reference'
	| 'case_reference'
	| 'case_similarity_search'
	| 'ueba_analysis'
	| 'enrichment'
	| 'metadata_discovery'
	| 'note'
	| 'sub_agent_trace'
	// keep the union OPEN for forward-compat / agent-emitted values:
	| (string & {})

/**
 * One row of `behavior_insights.insight_table`. `evidence_action_name` is the
 * join key to history/evidence — split on the FIRST `:` → `action_name` + `item_id`.
 */
export type InsightItem = {
	insight_area: InsightArea
	behavioral_finding: string
	assessment: InsightAssessment
	why_it_matters: string
	/**
	 * `null` = pure interpretation (no backing action). Otherwise `"<action_name>"`
	 * or `"<action_name>:<item_id>"`. Split on FIRST `:` only.
	 */
	evidence_action_name?: string | null
}

/**
 * `investigation.behavior_insights`. OPTIONAL on the parent (absent on older /
 * pre-feature investigations) — guard the whole object before reading. List
 * fields are required-but-can-be-`[]` → check `.length`.
 */
export type BehaviorInsights = {
	behavioral_fit: BehavioralFit
	primary_behavioral_driver: string
	behavior_summary: string
	insight_table: Array<InsightItem>
	confidence_drivers: Array<string>
	confidence_limiters: Array<string>
	analyst_validation_needed: Array<string>
	/** OPTIONAL/null even when insights present → render callout only if set. */
	detection_tuning_opportunity?: string | null
}

// --- wire contract (backend truth, ADR 0007) ---
/**
 * The `investigation` block as `_build_investigation_summary` emits it
 * (`rest_api/airapi/queries/case_management.py`) — FLAT, because the serializer
 * spreads the job envelope and the parsed conclusion into one dict.
 *
 * Every conclusion field is nullable and they move AS A UNIT: each one is
 * `conclusion.x if conclusion else None`, off the single `safe_parse_conclusion`
 * call. The wire cannot express that, which is why nothing consumes this type
 * directly — `toInvestigation` / `toInvestigationSummary`
 * (`case-management/lib/investigation-wire.ts`) fold it into the view models
 * below, and are the ONLY readers.
 *
 * The full-conclusion fields (plus `created_at`/`started_at`) are optional
 * because they live behind `include_full_conclusion`: the case-LIST endpoint
 * sends only `status`, `verdict`, `confidence` and `completed_at`.
 */
export type InvestigationWire = {
	status: InvestigationStatus
	verdict: Verdict | null
	confidence: number | null
	completed_at: string | null
	behavior_insights?: BehaviorInsights | null
	reasoning?: Array<string> | null
	triage_steps?: Array<string> | null
	narrative_report?: string | null
	created_at?: string | null
	started_at?: string | null
}

/** Case-list row as it arrives — the view model's `investigation` is folded. */
export type CaseWire = Omit<Case, 'investigation'> & {
	investigation: InvestigationWire | null
}

/** Case-detail envelope as it arrives (see {@link CaseDetailsResponse}). */
export type CaseDetailsWire = Omit<CaseDetailsResponse, 'investigation'> & {
	investigation?: InvestigationWire | null
}

/**
 * The part of a conclusion BOTH endpoints carry. `confidence` is non-null here
 * for the same reason `verdict` is: the backend model requires both, so a
 * conclusion that has one has the other (adapter gate).
 */
export type InvestigationConclusionSummary = {
	verdict: Verdict
	/** 0.0–1.0. */
	confidence: number
}

/**
 * A conclusion AIR® actually reached — the whole `InvestigationConclusion`
 * pydantic model, present or absent as one thing. Only the detail endpoint
 * carries the fields below the summary pair.
 */
export type InvestigationConclusion = InvestigationConclusionSummary & {
	/**
	 * Nullable WITHIN a present conclusion — `null` on older pre-feature
	 * investigations (the model defaults it to `None`). Guard it separately.
	 */
	behavior_insights: BehaviorInsights | null
	/** Key findings, one fact each. */
	reasoning: Array<string>
	/** Recommended analyst actions (plain list, no gating). */
	triage_steps: Array<string>
	/** Full written report, markdown. */
	narrative_report: string
}

/**
 * The investigation as the case LIST reports it: the job envelope plus the
 * summary pair. `status` is a job column and is INDEPENDENT of `conclusion` —
 * a completed job can carry no conclusion, and (transiently) vice versa.
 */
export type InvestigationSummary = {
	status: InvestigationStatus
	/** ISO-8601 string; null until the job completes. */
	completed_at: string | null
	/** `null` = the job reached no conclusion. The one guard. */
	conclusion: InvestigationConclusionSummary | null
}

/**
 * The investigation embedded in the case-detail response — the same job
 * envelope, with the full conclusion. Structurally an `InvestigationSummary`,
 * so anything reading the summary (the list chip) reads this too.
 */
export type Investigation = {
	status: InvestigationStatus
	/** ISO-8601 string; null on a job the backend has no timestamp for. */
	created_at: string | null
	/** ISO-8601 string; null until the job starts. */
	started_at: string | null
	/** ISO-8601 string; null until the job completes. */
	completed_at: string | null
	/** `null` = the job reached no conclusion. The one guard. */
	conclusion: InvestigationConclusion | null
}

/**
 * A curated evidence item backing the verdict. Either an attached finding (copy
 * of an action result + `comment`) or a note (`source_*` empty/null). `content`
 * is polymorphic on `evidence_type`. Verified against evidences.json (8 items).
 */
export type Evidence = {
	evidence_id: string
	alert_id: string
	tenant: string
	/** Which investigation attached it. OPTIONAL/null per backend-truth. */
	investigation_job_id: string | null
	evidence_type: EvidenceType
	/** Polymorphic on evidence_type — keep as `unknown`, narrow in renderers. */
	content: unknown
	/** The investigation's interpretation — *why it matters*. null for raw. */
	comment: string | null
	/** null/"" for freeform notes. */
	source_action_name: string | null
	/** "" in real data (doc said null). Treat ""/null as "no item id". */
	source_item_id: string | null
	/** ISO-8601 string. */
	created_at: string
}

/** Wrapped evidence response (live API envelope). */
export type CaseEvidenceResponse = {
	evidence: Array<Evidence>
}

/**
 * One entry in the full action history ("show your work"). Superset of Evidence.
 * `content` is polymorphic on `evidence_type`. Verified against
 * investigation_history.json (12 items).
 */
export type InvestigationHistoryItem = {
	/** Unique label within the investigation (may contain `__`). */
	action_name: string
	/** `""` when the action produced a single result (NOT null). */
	item_id: string
	tenant: string
	investigation_job_id: string
	/** The type it *would* have if attached as evidence. */
	evidence_type: EvidenceType
	/** Full raw result; polymorphic on evidence_type. */
	content: unknown
	/** ISO-8601 string. */
	created_at: string
}

/** Wrapped history response (live API envelope). */
export type InvestigationHistoryResponse = {
	investigation_history: Array<InvestigationHistoryItem>
}

/**
 * Flat matched-events map: `{ 'connector_id:event_type': docId[] }`. The
 * canonical shape every consumer (logs query, buildEventTypeMap, countLogs)
 * speaks. `CaseDetails.matched_events` is this shape post-normalize.
 */
export type MatchedEventsFlat = Record<string, Array<string>>

/** v1 wire shape — already flat. */
export type MatchedEventsV1 = MatchedEventsFlat

/**
 * v2 wire shape — wrapped (server-normalized, always returned by
 * `GET /case/<id>`). Both sub-maps key on `connector_id:event_type`.
 *
 * - `sources[key]`: the confirmed shape is an **array of objects**, each
 *   `{ doc_id, source }` carrying the full `_source` document. A bare
 *   `string[]` and a legacy `{ doc_ids: string[] }` object are also tolerated.
 * - `reference_only[key]`: an array of bare doc-id **strings** (the union is
 *   kept for tolerance).
 *
 * Note the structure is discriminated on **shape** (presence of `sources` /
 * `reference_only`), NOT on the numeric `version` — the real payload reports
 * `version: 1` for the wrapped shape.
 */
export type MatchedEventsV2 = {
	version?: number
	sources?: Record<
		string,
		| Array<string>
		| Array<{ doc_id: string; source?: unknown }>
		| { doc_ids: Array<string> }
	> | null
	reference_only?: Record<
		string,
		Array<string> | { doc_ids: Array<string> }
	> | null
}

/** Either wire shape, as it arrives from the backend (pre-normalize). */
export type MatchedEventsRaw =
	MatchedEventsV1 | MatchedEventsV2 | null | undefined
